<template>
  <div>
    <div class="intro-navbar py-3 text-white fixed-top w-100 mx-auto">
      <div class="container">
        <div class="d-flex justify-content-between align-items-center">
          <img src="/img/logopriadi.png" width="180" alt="Logo" />

          <div class="language-selector">
            <select class="form-select w-auto" v-model="currentLang" @change="switchLanguage" aria-label="Language">
              <option value="en">🇬🇧 English</option>
              <option value="id">🇮🇩 Indonesia</option>
              <option value="zh">🇨🇳 中文</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="slider-container">
      <transition name="fade" mode="out-in">
        <div :key="currentIndex" class="section d-flex align-items-center justify-content-center">
          <img :src="currentImage" :alt="$t('sloganHome')" class="img-fluid" />
        </div>
      </transition>
    </div>

    <div class="bottom-drawer">
      <!-- Toggler: Positioned outside the drawer -->
      <div
        class="toggle-tab bg-white text-center"
        :class="isDrawerToggled ? 'tab-open' : 'tab-closed'"
        @click="toggleDrawer">
        <i
          class="bi drawer-toggle-icon"
          :class="['text-secondary', 'bi-chevron-compact-' + (isDrawerToggled ? 'up' : 'down')]"></i>
      </div>
      <!-- Drawer -->
      <div class="drawer-container bg-white rounded-top shadow shadow-lg" :class="{ 'drawer-open': isDrawerToggled }">
        <div class="drawer-content pb-3">
          <div class="px-3">
            <p class="lead text-center">{{ $t('continueAs') }}</p>
            <div class="d-flex justify-content-center gap-2 mx-auto mb-3">
              <button
                class="role-card"
                @click="selectRole('individual')">
                <i class="bi bi-person-fill role-card-icon"></i>
                <span class="role-card-label">{{ $t('individual') }}</span>
              </button>
              <button
                class="role-card"
                @click="selectRole('hr')">
                <i class="bi bi-briefcase-fill role-card-icon"></i>
                <span class="role-card-label">{{ $t('corporate') }}</span>
              </button>
            </div>
            <p class="text-center intro-terms">
              {{ $t('agreeTermsIntro') }}
              <a
                href="https://priadi.id/terms-condition-apps.html"
                target="_blank"
                rel="noreferrer noopener"
                class="text-decoration-underline fw-bold">
                {{ $t('termsCondition') }}
              </a>
              {{ $t('and') }}
              <a
                href="https://priadi.id/privacy-policy-apps.html"
                target="_blank"
                rel="noreferrer noopener"
                class="text-decoration-underline fw-bold">
                {{ $t('privacyPolicy') }}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadLocaleMessages } from '../i18n'

export default {
  name: 'Intro',
  data() {
    return {
      isDrawerToggled: true,
      selectedLang: 'en',
      images: ['/img/intro-01.png', '/img/intro-02.png', '/img/intro-03.png', '/img/intro-04.png'],
      currentIndex: 0,
      intervalId: null,
    }
  },
  computed: {
    currentImage() {
      return this.images[this.currentIndex]
    },
    currentLang: {
      get() {
        return this.$store.getters.currentLocale
      },
      set(lang) {
        this.$store.dispatch('setLocale', lang)
      },
    },
  },
  async mounted() {
    // Detect browser language on first load if not already set
    if (!this.$store.getters.currentLocale) {
      const browserLang = navigator.language.split('-')[0]
      const fallback = ['en', 'id', 'zh'].includes(browserLang) ? browserLang : 'en'
      this.currentLang = fallback
      await this.switchLanguage()
    } else {
      this.selectedLang = this.currentLang
      await this.switchLanguage()
    }

    this.intervalId = setInterval(this.nextSection, 3000)
  },
  methods: {
    nextSection() {
      this.currentIndex = (this.currentIndex + 1) % this.images.length
    },
    toggleDrawer() {
      this.isDrawerToggled = !this.isDrawerToggled
    },
    selectRole(role) {
      this.$store.dispatch('setRole', role)
      if (this.$store.getters.getRole === 'hr') this.$router.push('/auth')
      else this.$router.push('/package')
    },
    async switchLanguage() {
      await loadLocaleMessages(this.currentLang)
    },
  },
  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  },
}
</script>

<style scoped>
.intro-navbar {
  max-width: var(--container-max);
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
}

.slider-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.slider-container .section {
  width: 100%;
  height: 100%;
}

.slider-container .section img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bottom-drawer {
  --toggle-tab-h: 40px;
  position: fixed;
  bottom: 0;
  width: 100%;
  max-width: var(--container-max);
  z-index: 1050;
}

/* Toggle tab styles */
.toggle-tab {
  width: 100px;
  height: var(--toggle-tab-h);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all 0.35s ease-in-out;
  pointer-events: auto;
}

.drawer-toggle-icon {
  font-size: var(--text-2xl);
}

.intro-terms {
  font-size: var(--text-sm);
}

.role-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-5) var(--space-4);
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: border-color var(--transition-base), box-shadow var(--transition-base);
}

.role-card:hover {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-md);
}

.role-card:active {
  transform: scale(0.97);
}

.role-card-icon {
  font-size: var(--text-2xl);
  color: var(--color-primary);
}

.role-card-label {
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
}

/* Fixed at bottom when closed */
.tab-closed {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1060;
}

/* Positioned above drawer when open */
.tab-open {
  position: absolute;
  top: calc(-1 * var(--toggle-tab-h));
  left: 50%;
  transform: translateX(-50%);
  z-index: 1060;
}

/* Add pointer-events only to the drawer container when closed */
.drawer-container {
  transform: translateY(100%);
  transition: transform 0.35s ease-in-out;
  border-top-left-radius: var(--radius-lg);
  border-top-right-radius: var(--radius-lg);
  padding-top: var(--toggle-tab-h);
  position: relative;
  pointer-events: auto;
}

/* Slide drawer up */
.drawer-open {
  transform: translateY(0);
}

/* 1. Declare transition effects */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out; /* Adjust duration and easing as needed */
}

/* 2. State when the element is entering */
.fade-enter-from {
  opacity: 0;
}

/* 3. State when the element is leaving */
.fade-leave-to {
  opacity: 0;
}
</style>
